import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  exportIssues,
  getFriendlyFilename,
  mapDedupedPageStatesToUniversalIssue,
  type UserFlowResults
} from '@deque/axe-devtools-issues-utils';
import {
  Modal,
  ModalContent,
  ModalFooter,
  RadioGroup,
  Button
} from '@deque/cauldron-react';
import { userFlowExport } from '@deque/axe-devtools-issues-utils/dist/export';
import { getApiServerUrl } from '../../common/utils/get-api-server-url';
import { type UserFlow } from '../../common/api-client';
import pkg from '../../../../package.json';
import rulesets from '@deque/accessibility-standards';
import { mapUniversalIssuesToExportIssue } from '../pages/UserFlowIssues.utils';
import useIssuesFilter from '../../common/hooks/useIssuesFilter';

interface SimpleExportIssuesModalProps {
  userFlow: UserFlow;
  userFlowResults: UserFlowResults;
  onClose: () => void;
  exportEntireFlow?: boolean;
}

type Format = 'json' | 'csv';

/**
 * A modal component for exporting user flow issues in JSON or CSV.
 *
 * @param {Object} props
 * @param {UserFlow} props.userFlow
 * @param {UserFlowResults} props.userFlowResults
 * @param {Function} props.onClose
 * @param {ExportEntireFlow} props.exportEntireFlow
 *
 * @returns {JSX.Element} The rendered modal component.
 *
 * @component
 *
 * @example
 * <SimpleExportIssuesModal
 *   exportFullFlow={exportFullFlow}
 *   userFlow={userFlow}
 *   userFlowResults={userFlowResults}
 *   onClose={handleClose}
 * />
 */
export default function SimpleExportIssueModal({
  userFlow,
  userFlowResults,
  onClose,
  exportEntireFlow = false
}: SimpleExportIssuesModalProps): JSX.Element {
  const { t } = useTranslation();
  const [format, setFormat] = useState<Format>('json');

  const universalIssues = useMemo(() => {
    return userFlowResults?.pageStateResults
      ? mapDedupedPageStatesToUniversalIssue(userFlowResults.pageStateResults)
      : [];
  }, [userFlowResults?.pageStateResults]);

  const { filteredIssues } = useIssuesFilter(universalIssues);

  const issues = useMemo(
    () => mapUniversalIssuesToExportIssue(filteredIssues, userFlow),
    [filteredIssues, userFlow]
  );

  const filename = useMemo(() => {
    if (!userFlowResults?.pageStateResults?.length || !userFlow?.name) {
      return '';
    }
    return getFriendlyFilename(
      userFlowResults.pageStateResults[0].url || '',
      userFlow.name
    );
  }, [userFlowResults, userFlow]);

  const handleSubmit = useCallback(() => {
    if (format === 'json' && exportEntireFlow) {
      userFlowExport({
        filename: `${filename}.json`,
        userFlow,
        results: userFlowResults,
        additionalProperties: {
          source: {
            productName: 'axe-devtools-html',
            productComponentName: 'web-ui',
            productVersion: pkg.version
          },
          standard: rulesets.wcag21aa.name,
          engine: 'axe-core',
          baseScreenshotURL: getApiServerUrl()
        }
      });
    } else if (format === 'json') {
      exportIssues({
        filename: `${filename}.json`,
        issues: issues,
        options: { baseScreenshotURL: getApiServerUrl() },
        format: 'json'
      });
    } else if (format === 'csv') {
      exportIssues({
        filename: `${filename}.csv`,
        issues: issues,
        format: 'csv-axe-devtools',
        options: { baseScreenshotURL: getApiServerUrl() }
      });
    }

    onClose();
  }, [format, filename, userFlow, userFlowResults, issues, onClose]);

  const handleFormatChange = ({ value }: { value: Format }) => setFormat(value);

  return (
    <Modal
      show
      heading={{ text: t('Export Saved Test and Issues'), level: 2 }}
      onClose={onClose}
    >
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <label id="export-format" className="Field__label">
            {t('Choose export format')}
          </label>

          <RadioGroup
            name="export-format"
            aria-label={t('Choose export format')}
            value={format}
            /* @ts-expect-error cauldron does not export the type we need here :( */
            onChange={handleFormatChange}
            radios={[
              { id: 'json', value: 'json', label: 'JSON' },
              { id: 'csv', value: 'csv', label: 'CSV' }
            ]}
          />
        </form>
      </ModalContent>
      <ModalFooter>
        <Button onClick={handleSubmit}>{t('Export')}</Button>
        <Button variant="secondary" onClick={onClose}>
          {t('Close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
